<template>
  <div>
    <eg-b-badge
      :id="id"
      v-b-popover.hover.top.html="popover"
      pill
      class="status-badge"
      :class="badgeClass"
      @click.stop="$emit('badge-click')"
    >
      <span class="icon-wrapper">
        <img
          v-if="showPlatformIcon && platformIcon"
          :src="platformIcon"
          :alt="platformLabel"
          class="platform-icon"
        />
        <eg-icon
          v-else-if="iconName"
          :icon="iconName"
          size="small"
          :class="{
            'spinning-icon':
              isGrading || status === 'Queued' || status === 'running',
          }"
        />
      </span>
      <span class="badge-text">{{ displayText }}</span>
    </eg-b-badge>

    <!-- Tooltip for Exported status -->
    <eg-b-tooltip v-if="platform && exportedAt" :target="id" triggers="hover">
      <div>
        {{
          $t("essays.lastExportedToOn", {
            platform: LMS_PROVIDERS[platform]?.label,
            date: formatDate(exportedAt, "MMM D, YYYY"),
          })
        }}
      </div>
    </eg-b-tooltip>
  </div>
</template>

<script>
import { LMS_PROVIDERS } from "@/constants";
import formatDate from "@/utils/formatDate";

export default {
  name: "EssayStatusBadge",

  props: {
    status: {
      type: String,
      required: true,
    },
    popover: {
      type: String,
      required: false,
      default: null,
    },
    id: {
      type: String,
      default: () => `status-badge-${Math.random().toString(36).substr(2, 9)}`,
    },
    isGrading: {
      type: Boolean,
      default: false,
    },
    platform: {
      type: String,
      default: null,
    },
    exportedAt: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      LMS_PROVIDERS,
    };
  },

  computed: {
    badgeClass() {
      if (this.status === "Queued") return "queued";
      if (this.status === "Failed") return "failed";

      switch (this.status) {
        case "Rejected":
        case "stopped":
        case "UnableToImport":
          return "rejected";
        case "Reviewed":
          return "reviewed";
        case "Exported":
          return "exported";
        case "Imported":
          return "imported";
        case "NotImported":
          return "not-imported";
        case "ExportedToLMS":
          return "exported";
        default:
          return "ready";
      }
    },

    iconName() {
      if (this.isGrading || this.status === "Queued") {
        return "refresh-rotate";
      }

      switch (this.status) {
        case "UnableToImport":
        case "Failed":
          return "risk-warning-error-danger";
        case "Reviewed":
          return "double-checkmark-read";
        case "Graded":
          return "check-circle";
        case "Imported":
          return "check";
        case "NotImported":
          return "file-download";
        case "Exported":
          return "export-dots";
        case "running":
          return "refresh-rotate";
        default:
          return "check-circle";
      }
    },

    displayText() {
      if (this.isGrading || this.status === "Queued") {
        return "Grading...";
      }

      switch (this.status) {
        case "Failed":
          return "Error";
        case "Rejected":
        case "Reviewed":
        case "Exported":
        case "Imported":
          return this.status;
        case "running":
          return "Running";
        case "stopped":
          return "Stopped";
        case "NotImported":
          return "Ready to Import";
        case "UnableToImport":
          return "Can't Import";
        case "ExportedToLMS":
          return "Exported";
        default:
          return "Ready for Review";
      }
    },

    showPlatformIcon() {
      return (
        (this.status === "ExportedToLMS" || this.status === "Exported") &&
        this.platform
      );
    },

    platformIcon() {
      return this.showPlatformIcon ? LMS_PROVIDERS[this.platform]?.icon : null;
    },

    platformLabel() {
      return this.showPlatformIcon ? LMS_PROVIDERS[this.platform]?.label : null;
    },
  },

  methods: {
    formatDate,
  },
};
</script>

<style lang="scss" scoped>
.status-badge {
  display: inline-flex;
  height: 24px;
  padding: 3px 8px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 12px;
  width: auto;
  font-size: var(--typography-font-size-fs-200);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-500, 500);
  line-height: 1;
  pointer-events: none;

  &.ready {
    background: var(--color-surface-primary-light-1);
    color: var(--color-text-primary);
  }

  &.reviewed {
    background: var(--colors-green-100);
    color: var(--color-surface-semantic-onSuccess);
  }

  &.exported {
    background: var(--color-surface-neutral-container-2, #f2f5f8);
    color: var(--color-text-neutral-secondary, #566370);
    pointer-events: auto;
  }

  &.imported {
    background: var(--color-surface-neutral-container-2);
    color: var(--color-text-neutral-neutral);
  }

  &.not-imported,
  &.queued {
    background: var(--colors-secondary-50);
    color: var(--colors-secondary-600);
  }

  &.rejected,
  &.failed {
    background: var(--color-surface-semantic-error-light);
    color: var(--color-text-semantic-onError);
    pointer-events: auto;
  }

  &.failed {
    cursor: pointer;
  }
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
}

.platform-icon {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.badge-text {
  white-space: nowrap;
}

.spinning-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
